import { assertNever } from "@glideapps/ts-necessities";
import { useWireAppTheme } from "hooks/use-wireapp-theme";
import { TailwindThemeProvider } from "sharedUI/lib/tailwind-theme-provider";
import { mergeTheme } from "sharedUI/lib/theme-utils";
import {
    defaultWireAppAccentContextOverlay,
    defaultWireAppAccentContextOverlayUndo,
    defaultWireAppImageBrightenContextOverlay,
    defaultWireAppImageDarkenContextOverlay,
} from "webapp/lib/theme/core-themes/wireapp-theme";
import type { OverlayTheme } from "webapp/lib/theme/overlay-theme";
import type { WireAppTheme } from "webapp/lib/theme/theme";
import { SectionStyleProvider } from "webapp/ui/views/wire-container/wire-container";
import { HeroImageEffect, UIBackgroundStyle } from "wire/types";

export function useWireOverlay(style: UIBackgroundStyle, content: React.ReactNode): React.ReactNode {
    let theme = useWireAppTheme();

    let overlay: (Partial<WireAppTheme> & OverlayTheme) | undefined;

    switch (style) {
        case UIBackgroundStyle.Accent:
            overlay = defaultWireAppAccentContextOverlay;
            break;
        case UIBackgroundStyle.Card:
        case UIBackgroundStyle.Highlight:
            overlay = defaultWireAppAccentContextOverlayUndo;
            break;
        case UIBackgroundStyle.Dark:
            overlay = defaultWireAppImageDarkenContextOverlay;
            break;
        case UIBackgroundStyle.Image:
            overlay = defaultWireAppImageDarkenContextOverlay;
            break;
        case UIBackgroundStyle.None:
        case UIBackgroundStyle.Soft:
            break;
    }

    if (overlay !== undefined) {
        theme = mergeTheme(theme, [overlay]);
    }
    return (
        <SectionStyleProvider value={style}>
            <TailwindThemeProvider theme={theme}>{content}</TailwindThemeProvider>
        </SectionStyleProvider>
    );
}

export const WireSectionThemer: React.FC<{
    backgroundStyle: UIBackgroundStyle;
    effect?: HeroImageEffect;
    className?: string;
}> = p => {
    const { backgroundStyle, effect, children, className } = p;
    let theme = useWireAppTheme();

    let overlay: (Partial<WireAppTheme> & OverlayTheme) | undefined;

    switch (backgroundStyle) {
        case UIBackgroundStyle.Accent:
            overlay = defaultWireAppAccentContextOverlay;
            break;
        case UIBackgroundStyle.Card:
        case UIBackgroundStyle.Highlight:
            overlay = defaultWireAppAccentContextOverlayUndo;
            break;
        case UIBackgroundStyle.Dark:
            overlay = defaultWireAppImageDarkenContextOverlay;
            break;
        case UIBackgroundStyle.Image:
            if (effect === HeroImageEffect.Lighten) {
                overlay = defaultWireAppImageBrightenContextOverlay;
            } else {
                overlay = defaultWireAppImageDarkenContextOverlay;
            }
            break;
        case UIBackgroundStyle.None:
        case UIBackgroundStyle.Soft:
            break;
        default:
            assertNever(backgroundStyle);
    }

    if (overlay !== undefined) {
        theme = mergeTheme(theme, [overlay]);
    }
    return (
        <SectionStyleProvider value={backgroundStyle}>
            <TailwindThemeProvider theme={theme} className={className}>
                {children}
            </TailwindThemeProvider>
        </SectionStyleProvider>
    );
};
